import {
  ArrowRight,
  ArrowUpRight,
  Bell,
  Calendar,
  ChartNoAxesCombined,
  Check,
  ChevronDown,
  ChevronRight,
  Clock,
  Copy,
  Download,
  ExternalLink,
  FileText,
  FolderKanban,
  House,
  LayoutDashboard,
  ListFilter,
  Info,
  Link2,
  LibraryBig,
  Lock,
  Menu,
  MoreHorizontal,
  PanelLeft,
  Pencil,
  Pin,
  Plus,
  RefreshCw,
  ScrollText,
  Search,
  Settings,
  ShieldCheck,
  Trash2,
  TriangleAlert,
  Upload,
  User,
  Wallet,
  X,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

/**
 * 全站图标单一入口。
 *
 * 统一约定（新增图标请一并遵守）：
 * - 24×24 网格、描边风格、`currentColor`，因此图标颜色自动跟随文字色
 * - 默认描边 1.75，与旧版手绘图标一致；仅在极小尺寸（≤14px）才允许 2
 * - 尺寸只用 16 / 18 / 20 三档；跟随文字时用 `inline` 类内联对齐
 * - 纯装饰图标默认 `aria-hidden`；表达含义时传 `label`
 */
const registry = {
  // 导航
  overview: LayoutDashboard,
  workspace: House,
  management: ChartNoAxesCombined,
  project: FolderKanban,
  contract: ScrollText,
  fileText: FileText,
  settlement: Wallet,
  foundation: LibraryBig,
  system: ShieldCheck,
  settings: Settings,
  // 顶栏 / 外壳
  search: Search,
  bell: Bell,
  plus: Plus,
  panel: PanelLeft,
  menu: Menu,
  pin: Pin,
  // 动作与状态
  arrowRight: ArrowRight,
  arrowUpRight: ArrowUpRight,
  info: Info,
  link: Link2,
  chevronRight: ChevronRight,
  chevronDown: ChevronDown,
  check: Check,
  close: X,
  more: MoreHorizontal,
  alert: TriangleAlert,
  edit: Pencil,
  trash: Trash2,
  download: Download,
  upload: Upload,
  calendar: Calendar,
  clock: Clock,
  external: ExternalLink,
  copy: Copy,
  refresh: RefreshCw,
  filter: ListFilter,
  lock: Lock,
  user: User,
} satisfies Record<string, LucideIcon>;

export type IconName = keyof typeof registry;

interface IconProps {
  name: IconName;
  /** 16 / 18 / 20 三档，默认 20 */
  size?: number;
  strokeWidth?: number;
  className?: string;
  /** 传了才暴露给读屏；不传视为装饰性图标 */
  label?: string;
}

export function Icon({ name, size = 20, strokeWidth = 1.75, className, label }: IconProps) {
  const Glyph = registry[name];
  return (
    <Glyph
      className={className}
      size={size}
      strokeWidth={strokeWidth}
      aria-hidden={label ? undefined : true}
      {...(label ? { role: 'img' as const, 'aria-label': label } : {})}
    />
  );
}

/** 侧栏 / 顶栏使用的语义图标名（与旧 WorkspaceIcon 的调用点保持兼容） */
export type WorkspaceIconName =
  | 'overview'
  | 'workspace'
  | 'management'
  | 'project'
  | 'contract'
  | 'settlement'
  | 'foundation'
  | 'system'
  | 'search'
  | 'bell'
  | 'plus'
  | 'panel'
  | 'settings'
  | 'menu'
  | 'pin';

/**
 * 外壳图标：挂在 `.workspace-icon` 上，具体像素尺寸由 CSS 控制，
 * 因此这里不传 size（交给 CSS 覆盖），只统一描边与颜色。
 */
export function WorkspaceIcon({ name }: { name: WorkspaceIconName }) {
  return <Icon className="workspace-icon" name={name} strokeWidth={1.75} />;
}
